import './ReturnToQuotePopup.css';

import { addDays, formatISO, startOfDay } from 'date-fns';
import React, { useState } from 'react';

// import { Link } from 'react-router-dom';
import Button from '../../../../components/Button/Button';
import CustomDatePicker from '../../../../components/DatePicker/DatePicker';
import Loading from '../../../../components/Loading/Loading';
import Modal from '../../../../components/Modal/Modal';
import { useUpdateEffectiveDateMutation } from '../../../../generated/graphql';
import { getApplicationFinalizedMessage } from '../../../../utils/isApplicationFinalizedError';
import { ErrorPage } from '../../..';

interface Props {
  isQuoteExpired: boolean;
  applicationGroupId: string;
  quoteExpireDate: string;
  closeAction: () => void;
}

const today = new Date();
today.setHours(0);
today.setMinutes(0);
today.setSeconds(0);

export const ReturnToQuotePopup: React.FC<Props> = (props) => {
  const {
    isQuoteExpired,
    applicationGroupId,
    quoteExpireDate,
    closeAction
  } = props;
  const quoteExpiredMessage = isQuoteExpired
    ? 'Your Quote has expired, please submit a new application'
    : 'The previously requested effective date is no longer valid. \n Please choose a new date:';

  const [effectiveDate, setNewEffectiveDate] = useState<undefined | Date>(
    undefined
  );
  const [isEffectiveDateValid, setisEffectiveDateValid] = useState(true);
  const [isContinueDisabled, setIsContinueDisabled] = useState(false);
  // const [
  //   isStartNewApplicationDisabled,
  //   setisStartNewApplicationDisabled
  // ] = useState(!isQuoteExpired);

  const [
    updateEffectiveDateMutation,
    { loading: isUpdateEffectiveDateLoading }
  ] = useUpdateEffectiveDateMutation();
  const [isVerifyingEffectiveDate, setIsVerifyingEffectiveDate] = useState(
    false
  );
  const [
    applicationFinalizedMessage,
    setApplicationFinalizedMessage
  ] = useState<string | undefined>(undefined);

  const continueAction = async () => {
    // condition to prevent user from clicking continue
    // when they have invalid effective date
    if (effectiveDate && effectiveDate >= today) {
      setIsVerifyingEffectiveDate(true);
      setApplicationFinalizedMessage(undefined);

      let updateEffectiveDateData;
      try {
        const result = await updateEffectiveDateMutation({
          variables: {
            applicationGroupId: applicationGroupId,
            newEffectiveDate: formatISO(startOfDay(effectiveDate), {
              representation: 'date'
            })
          }
        });
        updateEffectiveDateData = result.data;
      } catch (err) {
        // The spinner replaces the whole popup while verifying, so leaving the
        // flag set on the way out strands the customer on it with the date they
        // chose unreachable and no way back except reloading the page.
        setIsVerifyingEffectiveDate(false);
        const finalizedMessage = getApplicationFinalizedMessage(err);
        if (finalizedMessage) {
          setApplicationFinalizedMessage(finalizedMessage);
          return;
        }
        throw err;
      }

      /* istanbul ignore if -- async error handler untestable in React Testing Library */
      if (!updateEffectiveDateData) {
        throw new Error('updateEffectiveDateData should be ready');
      }
      /* istanbul ignore if -- async error handler untestable in React Testing Library */
      if (
        updateEffectiveDateData &&
        updateEffectiveDateData.updateEffectiveDate === false
      ) {
        throw new Error('Update not successful');
      }
      closeAction();
    } else if (!effectiveDate) {
      setisEffectiveDateValid(false);
    }
  };

  if (!quoteExpireDate) {
    return <ErrorPage message={'quoteExpireDate does not exist'} />;
  }

  const quoteExpireDateObject = new Date(quoteExpireDate);

  const checkEffectiveDate = (effectiveDate: Date | null) => {
    if (effectiveDate) {
      setNewEffectiveDate(effectiveDate);
      setisEffectiveDateValid(true);
      if (effectiveDate > quoteExpireDateObject) {
        setIsContinueDisabled(true);
        // setisStartNewApplicationDisabled(false);
      } else {
        setIsContinueDisabled(false);
        // setisStartNewApplicationDisabled(true);
      }
    } else {
      setisEffectiveDateValid(false);
    }
  };

  if (isUpdateEffectiveDateLoading || isVerifyingEffectiveDate) {
    return (
      <div className="modal">
        <div className="modal-content loading">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <Modal closeAction={undefined} onAction={undefined}>
      <div className="ReturnToQuotePopup">
        {applicationFinalizedMessage ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center justify-between">
            <div>
              <p className="font-bold">Unable to proceed</p>
              <p>{applicationFinalizedMessage}</p>
            </div>
            <button
              className="ml-4 shrink-0 underline font-bold text-red-700"
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>
          </div>
        ) : null}
        <p className="quoteExpired mb-4">{quoteExpiredMessage}</p>
        {!isQuoteExpired ? (
          <div className="effectiveDateExpired">
            <CustomDatePicker
              id="SelectEffectiveDate"
              leftLabel={'When would you like your new coverage to start?'}
              rightLabel={''}
              onChange={checkEffectiveDate}
              name={'calendar'}
              value={effectiveDate}
              minDate={today}
              maxDate={addDays(today, 60)}
              helperText={''}
              readOnly={false}
            />
            {!isEffectiveDateValid ? (
              <p className="text-base text-red-600 mt-2 mb-0">
                Please select a valid date.
              </p>
            ) : null}

            <p className="mt-4 mx-auto">
              If the new requested coverage start date selected is after
              <br></br>{' '}
              {formatISO(quoteExpireDateObject, { representation: 'date' })}, a
              new application must be submitted.
            </p>
          </div>
        ) : null}
        <div className="flex flex-col align-center">
          {!isQuoteExpired ? (
            <Button
              id="ContinueButton"
              className="inline-block bg-tertiary text-base uppercase pointer font-bold mt-2 mx-auto w-2/5 px-6 py-3 font-raleway sm:text-mobileFont rounded text-white"
              onClick={continueAction}
              disabled={isContinueDisabled}
              buttonText="Continue"
            />
          ) : null}
          {/* <Link
            id="NewApplicationButton"
            className={`${
              isStartNewApplicationDisabled ? 'disabled-link' : ''
            } inline-block text-base uppercase pointer mt-4 px-6 py-3 font-raleway sm:text-mobileFont rounded no-underline hover:underline hover:text-blue-700`}
            to={'/'}
          >
            Start New Application
          </Link> */}
        </div>
      </div>
    </Modal>
  );
};
